import React, { useState, useEffect } from 'react';
import Sidebar from 'components/ui/Sidebar';
import Icon from 'components/AppIcon';
import ExecutiveMetrics from './components/ExecutiveMetrics';
import FinancialOverview from './components/FinancialOverview';
import PracticeAnalytics from './components/PracticeAnalytics';
import PartnerPerformance from './components/PartnerPerformance';
import StrategicPlanning from './components/StrategicPlanning';
import AdministrativeControls from './components/AdministrativeControls';

const PartnerPortal = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [isSecureView, setIsSecureView] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  const periods = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' }
  ];

  const formatTime = (date) => {
    return date?.toLocaleTimeString('en-KE', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const formatDate = (date) => {
    return date?.toLocaleDateString('en-KE', { 
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleSecureAuthentication = () => {
    // Enhanced authentication for sensitive financial data
    setIsSecureView(!isSecureView);
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      
      <div className="md:ml-64 transition-layout">
        {/* Header */}
        <header className="bg-surface border-b border-border px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Crown" size={24} color="white" />
                </div>
                <div>
                  <h1 className="text-2xl font-heading font-bold text-text-primary">Partner Portal</h1>
                  <p className="text-text-secondary">
                    {formatDate(currentTime)} • {formatTime(currentTime)}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Period Selector */}
              <select 
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target?.value)}
                className="bg-surface border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {periods?.map((period) => (
                  <option key={period?.value} value={period?.value}>
                    {period?.label}
                  </option>
                ))}
              </select>

              {/* Secure View Toggle */}
              <button 
                onClick={handleSecureAuthentication}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-smooth ${
                  isSecureView 
                    ? 'bg-success-50 text-success-700 border border-success-200' :'bg-warning-50 text-warning-700 border border-warning-200'
                }`}
              >
                <Icon name={isSecureView ? 'ShieldCheck' : 'Shield'} size={16} />
                <span>{isSecureView ? 'Secure View' : 'Standard View'}</span>
              </button>

              {/* Notifications */}
              <button className="relative p-2 text-text-secondary hover:text-text-primary transition-smooth">
                <Icon name="Bell" size={20} />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-accent rounded-full"></span>
              </button>
              
              {/* Profile */}
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} color="white" />
                </div>
                <div className="hidden md:block">
                  <p className="text-sm font-medium text-text-primary">Senior Partner</p>
                  <p className="text-xs text-text-secondary">Executive Access</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-6">
          {/* Executive Metrics */}
          <ExecutiveMetrics selectedPeriod={selectedPeriod} isSecureView={isSecureView} />

          {/* Dashboard Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
            {/* Financial Overview */}
            <div className="xl:col-span-2">
              <FinancialOverview selectedPeriod={selectedPeriod} isSecureView={isSecureView} />
            </div>

            {/* Practice Analytics */}
            <div className="xl:col-span-1">
              <PracticeAnalytics selectedPeriod={selectedPeriod} />
            </div>
          </div>

          {/* Performance and Strategic Planning */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <PartnerPerformance selectedPeriod={selectedPeriod} isSecureView={isSecureView} />
            <StrategicPlanning selectedPeriod={selectedPeriod} />
          </div>

          {/* Administrative Controls */}
          {isSecureView && (
            <AdministrativeControls />
          )}

          {/* Mobile Alert */}
          <div className="md:hidden bg-primary-50 border border-primary-200 rounded-lg p-4 mt-6">
            <div className="flex items-center space-x-3">
              <Icon name="Smartphone" size={20} className="text-primary" />
              <div>
                <h3 className="text-sm font-medium text-primary">Mobile Access</h3>
                <p className="text-xs text-primary-700 mt-1">
                  Key metrics and alerts optimized for client meetings and court appearances
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PartnerPortal;